export const project = 'asc'; // need this for when we dynamically import utils

/**
 *
 * @param {Object} win - The window object.
 * @param {Object} payload - The payload to send coming from mappings.
 *
 */

// We need to add the gtag function for the new integration, but let's avoid override it if already there
window.gtag =
	window.gtag ||
	function gtag() {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer.push(arguments);
	};

const adwordsIds = new Promise((resolve) => {
	// the asc_datalayer object should be already created by this time
	if (window?.asc_datalayer?.measurement_ids?.length) {
		resolve(window.asc_datalayer.measurement_ids);
	} else {
		window.addEventListener('googleAdwordsConversionIdsReady', (event) => {
			resolve(event.detail);
		});
	}
});

export const sending = ({ win, data: basePayload, preview, eventData }) => {
	const payload = {
		...basePayload
	};

	if (payload.page_type && win?.DDC?.dataLayer?.page?.pageInfo?.isVlp) {
		payload.page_type = 'itemlist';
	}

	// Event Viewer
	const reportsForEventViewer = [];
	const reportsForNewRelic = [];

	if (!preview) {
		if (payload.event === 'asc_form_submission') {
			win.gtag('set', 'user_data', {
				sha256_email_address: eventData?.fields?.email,
				sha256_phone_number: eventData?.fields?.phone
			});

			reportsForEventViewer.push({
				clientEventName: 'user_data',
				payload,
				sender: 'win.gtag'
			});

			reportsForNewRelic.push({
				clientEventName: 'user_data',
				sender: 'win.gtag'
			});
		}
		// utils/dataMapper.js can handle an array of promises for reporting purposes
		reportsForEventViewer.push(
			new Promise((resolve) => {
				// if we have g-ids from the page or the event in the web integration then proceed
				adwordsIds.then((gids) => {
					win.gtag('event', payload.event, {
						send_to: gids,
						...payload
					});
					resolve({
						clientEventName: payload.event,
						measurement_ids: gids,
						payload,
						sender: 'window.dataLayer'
					});
				});
			})
		);
	}

	// PUSH events
	win.asc_datalayer.events.push(payload);

	const sender = 'asc_datalayer.events';

	reportsForEventViewer.push({
		clientEventName: payload.event,
		payload,
		sender
	});

	reportsForNewRelic.push({
		clientEventName: payload.event,
		sender
	});

	return {
		reportsForEventViewer,
		reportsForNewRelic
	};
};

const getVehicle = (eventData, vehicles) =>
	vehicles.length === 1 ? vehicles[0] : eventData?.vehicle;

export const getLanguage = ({ window }) =>
	window.DDC.dataLayer.page.attributes?.locale?.substring(0, 2);

export const getBrand = ({ window }) =>
	window.DDC.dataLayer.site.franchises.join('|');

const getPriceFromVehicle = (vehicle) => {
	let price;
	if (vehicle) {
		price =
			vehicle.newOrUsed === 'new' ? vehicle.msrp : vehicle.retailValue;
	}
	return price;
};

export const getPrice = ({ eventData, vehicles = [] }) =>
	getPriceFromVehicle(getVehicle(eventData, vehicles));

const getConditionFromVehicle = (vehicle) => {
	let condition;
	if (vehicle) {
		if (vehicle.newOrUsed === 'used') {
			condition = vehicle.certified === 'true' ? 'cpo' : 'used';
		} else if (vehicle.newOrUsed === 'new') {
			condition = 'new';
		}
	}
	return condition;
};

export const getCondition = ({ eventData, vehicles = [] }) =>
	getConditionFromVehicle(getVehicle(eventData, vehicles));

const convertDateFormat = (mmddyyy) => {
	if (!mmddyyy) return mmddyyy;
	const date = new Date(mmddyyy);
	const year = date.getFullYear();
	const month = date.getMonth() + 1;
	const day = date.getDate();
	const formatted = `${year}/${`${month}`.padStart(
		2,
		'0'
	)}/${`${day}`.padStart(2, '0')}`;
	return formatted;
};

/**
 * Determines the inventory type based on the vehicles array
 * @param {Array} vehicles - Array of vehicle objects from DDC.dataLayer.vehicles
 * @returns {string|undefined} - 'new', 'used', 'cpo', 'all', or undefined if no vehicles
 */
export const getTypeFromVehicles = (vehicles = []) => {
	if (!vehicles.length) {
		return undefined;
	}

	const hasNew = vehicles.some((vehicle) => vehicle.newOrUsed === 'new');
	const hasUsed = vehicles.some((vehicle) => vehicle.newOrUsed === 'used');
	const allCertified =
		hasUsed &&
		!vehicles.some(
			(vehicle) =>
				vehicle.newOrUsed === 'used' &&
				vehicle.certified !== 'true' &&
				vehicle.certified !== true
		);

	// Mix of new and any type of used (including cpo)
	if (hasNew && hasUsed) {
		return 'all';
	}

	// Only new vehicles
	if (hasNew) {
		return 'new';
	}

	// Only certified (all used vehicles are certified)
	if (allCertified) {
		return 'cpo';
	}

	// Mix of cpo and used, or only used - return 'used'
	if (hasUsed) {
		return 'used';
	}

	return undefined;
};

export const getItemlistType = ({ window }) => {
	const pageAlias = window.DDC.dataLayer.page.pageInfo.pageName;
	const types = {
		_NEW: 'new',
		_USED: 'used',
		_CERTIFIED_USED: 'cpo',
		_ALL: 'all'
	};
	let type;
	Object.keys(types).forEach((key) => {
		type = pageAlias.indexOf(key) > -1 ? types[key] : type;
	});

	// Fallback to determining type from vehicles array if page alias didn't match
	if (!type) {
		type = getTypeFromVehicles(window.DDC.dataLayer.vehicles);
	}

	return type;
};

export const getFacetRange = ({ eventData }) => {
	return eventData.isSlider &&
		eventData.clickedFacetValue?.indexOf?.('-') > -1
		? `minmax_${eventData.clickedFacetName}`
		: '';
};

export const getInventoryDate = (options) => {
	const { eventData: { vehicle = {} } = {}, window } = options;
	if (
		window.DDC.dataLayer.vehicles.length === 1 ||
		Object.keys(vehicle).length ||
		window.DDC.dataLayer?.confirmation?.vehicle?.inventoryDate
	) {
		return convertDateFormat(
			window.DDC.dataLayer.vehicles?.[0]?.inventoryDate ||
				vehicle?.inventoryDate ||
				window.DDC.dataLayer?.confirmation?.vehicle?.inventoryDate
		);
	}
	return '';
};

let uuidVehicleMap; // its a cache to make future uuid->vhicle lookups faster
export const getVehicleFromUuid = ({ eventData, vehicles }) => {
	const { uuid } = eventData;
	if (uuid) {
		if (!uuidVehicleMap) {
			uuidVehicleMap = vehicles.reduce((map, vehicle) => {
				return {
					...map,
					[vehicle.uuid]: vehicle
				};
			}, {});
		}
		return uuidVehicleMap[uuid] || ''; // fallback to blank if uuid is not found in vechiles array
	}
	return ''; // fallback to blank if uuid is not provided in eventData
};

export const getItemIdFromUuid = ({ eventData }) => {
	return eventData.uuid || '';
};
export const getStockNumberFromUuid = ({ eventData, vehicles }) => {
	const vehicle = getVehicleFromUuid({ eventData, vehicles });
	return vehicle ? vehicle.stockNumber : '';
};
export const getConditionFromUuid = ({ eventData, vehicles }) =>
	getConditionFromVehicle(getVehicleFromUuid({ eventData, vehicles }));

export const getColorFromUuid = ({ eventData, vehicles }) => {
	const vehicle = getVehicleFromUuid({ eventData, vehicles });
	return vehicle ? vehicle.exteriorColor : '';
};
export const getYearFromUuid = ({ eventData, vehicles }) => {
	const vehicle = getVehicleFromUuid({ eventData, vehicles });
	return vehicle ? vehicle.modelYear : '';
};
export const getMakeFromUuid = ({ eventData, vehicles }) => {
	const vehicle = getVehicleFromUuid({ eventData, vehicles });
	return vehicle ? vehicle.make : '';
};
export const getModelFromUuid = ({ eventData, vehicles }) => {
	const vehicle = getVehicleFromUuid({ eventData, vehicles });
	return vehicle ? vehicle.model : '';
};
export const getTrimFromUuid = ({ eventData, vehicles }) => {
	const vehicle = getVehicleFromUuid({ eventData, vehicles });
	return vehicle ? vehicle.trim : '';
};
export const getTypeFromUuid = ({ eventData, vehicles }) => {
	const vehicle = getVehicleFromUuid({ eventData, vehicles });
	return vehicle ? vehicle.bodyStyle : '';
};
export const getFuelType = ({ window, eventData, vehicles }) => {
	// some logic from getReplacement to duplicate how fuelType was set up prior to this mapping change
	const vehicle =
		// single vehicle lookups should only happen on pages with 1 vehicle
		(window?.DDC?.dataLayer?.vehicles?.length === 1 &&
			window?.DDC?.dataLayer?.vehicles?.[0]) ||
		// form confirmation pages
		window?.DDC?.dataLayer?.confirmation?.vehicle;

	let fuelType =
		vehicle?.normalFuelType ||
		eventData?.normalFuelType ||
		eventData?.vehicle?.normalFuelType;

	if (!fuelType) {
		const vehicleFromUuid = getVehicleFromUuid({ eventData, vehicles });
		fuelType = vehicleFromUuid ? vehicleFromUuid.normalFuelType : '';
	}

	if (fuelType) {
		fuelType = fuelType.toLowerCase();
	}

	// if no vehicle, fuelType should stay empty string
	// for pages that do not have vehicle data
	// otherwise on those pages all vehicle attributes will be empty string and fuelType will be unknown
	// which is not what the client wants
	if (
		vehicle?.normalFuelType ||
		eventData?.uuid ||
		eventData?.vehicle?.uuid ||
		eventData?.normalFuelType ||
		eventData?.vehicle?.normalFuelType
	) {
		// ASC spec: gas, diesel, liquified_natural_gas, electric, propane, nitrogen, methane, unknown
		if (fuelType === 'gasoline' || fuelType?.includes?.('hybrid')) {
			fuelType = 'gas';
		} else if (fuelType === 'natural gas') {
			fuelType = 'liquified_natural_gas';
		} else if (fuelType !== 'diesel' && fuelType !== 'electric') {
			fuelType = 'unknown';
		}
	}

	return fuelType;
};
export const getVehicleItems = ({ vehicles = [] }) => {
	const items = [];
	vehicles.forEach((vehicle) => {
		const item = {
			item_id: (vehicle.vin || vehicle.uuid)?.toLowerCase?.(),
			item_number: vehicle.stockNumber?.toLowerCase?.(),
			item_condition: getCondition({
				eventData: {
					vehicle
				}
			})?.toLowerCase?.(),
			item_year: vehicle.modelYear,
			item_make: vehicle.make?.toLowerCase?.(),
			item_model: vehicle.model?.toLowerCase?.(),
			item_variant: vehicle.trim?.toLowerCase?.(),
			item_color: vehicle.exteriorColor?.toLowerCase?.(),
			item_type: vehicle.bodyStyle?.toLowerCase?.(),
			item_fuel_type: getFuelType({
				window,
				eventData: {
					uuid: vehicle.uuid
				},
				vehicles
			}),
			item_inventory_date: convertDateFormat(vehicle.inventoryDate),
			item_price: getPrice({
				eventData: {
					vehicle
				}
			})
		};
		items.push(item);
	});
	return items;
};
export const getInventoryDateFromUuid = ({ eventData, vehicles }) => {
	const vehicle = getVehicleFromUuid({ eventData, vehicles });
	return vehicle ? convertDateFormat(vehicle.inventoryDate) : '';
};
export const getPriceFromUuid = ({ eventData, vehicles }) =>
	getPriceFromVehicle(getVehicleFromUuid({ eventData, vehicles }));

// at one point in DDC history the copy page tool was making forms with trackingId and formId as a 32 character string
// the client wants these forms labeled as custom
// we are unable to fix them as they are all over DVS and will be hard to identify and then actually know what form
// they were based on before copy page tool messed them up
const isFunkyForm = (customFormId, trackingId) =>
	customFormId?.length === 32 && customFormId === trackingId;

// "form_name": "[[eventData.trackingId]]  >> {{EMPTYSTRING}}",
export const getFormName = ({ eventData }) => {
	let formName = eventData?.trackingId || '';

	if (isFunkyForm(eventData.customFormId, eventData.trackingId)) {
		formName = 'custom';
	}

	return formName;
};

// "form_type": "[[mapping.form.formType.[[eventData.trackingId]]]] >> [[eventData.customFormId]] >> {{EMPTYSTRING}}",
export const getFormType = ({ eventData, mapping }) => {
	let formType =
		mapping?.form?.formType[eventData?.trackingId] ||
		eventData?.customFormId ||
		'';

	if (isFunkyForm(eventData.customFormId, eventData.trackingId)) {
		formType = 'custom';
	}

	return formType;
};
